/**
 * Изменение размера модального окна (resize по краям и углам).
 * Направление определяется по позиции курсора относительно краёв .modal-content.
 * Зона захвата расширена через ::before псевдоэлемент (.modal-resizable).
 * Размеры сохраняются/загружаются из localStorage по ID модального окна.
 */

/**
 * Инициализирует resize модального окна.
 * @param {ModalManager} manager
 */
export function makeResizable(manager) {
    if (manager.type !== 'modal') return;

    const content = manager.modalDialog?.querySelector('.modal-content');
    if (!content) return;

    // Фиксируем минимальные размеры только при первом открытии
    if (manager._minWidth === 0) {
        manager._minWidth = manager.modalDialog.getBoundingClientRect().width;
    }
    if (manager._minHeight === 0) {
        manager._minHeight = content.getBoundingClientRect().height;
    }

    // Загружаем сохранённые размеры из localStorage
    loadModalSize(manager);

    // Добавляем класс для ::before псевдоэлемента (расширяет зону hovera)
    content.classList.add('modal-resizable');

    // Не дублируем слушатели
    if (manager._resizeListenersAttached) return;
    manager._resizeListenersAttached = true;

    // Динамически меняем курсор при приближении к краям modal-content
    manager.modalElement.addEventListener('mousemove', (e) => {
        if (manager._resizeDirection) return; // не менять курсор во время resize
        const dir = getResizeDirection(manager, e);
        manager.modalElement.style.cursor = dir ? manager.constructor.RESIZE_CURSORS[dir] : '';
    });

    // Начинаем resize при mousedown вблизи края
    manager.modalElement.addEventListener('mousedown', (e) => {
        const dir = getResizeDirection(manager, e);
        if (dir) onResizeMouseDown(manager, e, dir);
    });

    if (manager.log) console.log('Modal resize initialized, minWidth:', manager._minWidth, 'minHeight:', manager._minHeight);
}

/**
 * Определяет направление resize по позиции курсора относительно краёв .modal-content.
 * @param {ModalManager} manager
 * @param {MouseEvent} event
 * @returns {string|null}
 */
export function getResizeDirection(manager, event) {
    const content = manager.modalDialog?.querySelector('.modal-content');
    if (!content) return null;

    const rect = content.getBoundingClientRect();
    const edge = manager.constructor.RESIZE_EDGE;
    const { clientX: x, clientY: y } = event;

    // Курсор вне расширенной зоны modal-content
    if (x < rect.left - edge || x > rect.right + edge ||
        y < rect.top - edge || y > rect.bottom + edge) return null;

    // Курсор внутри content, далеко от краёв — не resize
    if (x > rect.left + edge && x < rect.right - edge &&
        y > rect.top + edge && y < rect.bottom - edge) return null;

    // Определяем направление по близости к каждому краю
    let dir = '';
    if (y <= rect.top + edge) dir += 'n';
    if (y >= rect.bottom - edge) dir += 's';
    if (x <= rect.left + edge) dir += 'w';
    if (x >= rect.right - edge) dir += 'e';

    return dir || null;
}

/**
 * @param {ModalManager} manager
 * @param {MouseEvent} event
 * @param {string} direction
 */
export function onResizeMouseDown(manager, event, direction) {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();

    manager._isResizing = true;
    manager._resizeDirection = direction;
    manager._resizeStartX = event.clientX;
    manager._resizeStartY = event.clientY;

    const content = manager.modalDialog.querySelector('.modal-content');
    manager._resizeStartWidth = manager.modalDialog.getBoundingClientRect().width;
    manager._resizeStartHeight = content.getBoundingClientRect().height;

    // Получаем текущее смещение из transform
    const style = window.getComputedStyle(manager.modalElement);
    const matrix = style.transform === 'none' ? null : style.transform.match(/matrix.*\((.+)\)/);
    if (matrix) {
        const values = matrix[1].split(', ');
        manager._resizeStartTransformX = parseFloat(values[4]);
        manager._resizeStartTransformY = parseFloat(values[5]);
    } else {
        manager._resizeStartTransformX = manager.dragOffsetX || 0;
        manager._resizeStartTransformY = manager.dragOffsetY || 0;
    }

    document.body.style.userSelect = 'none';
    document.body.style.cursor = manager.constructor.RESIZE_CURSORS[direction];
    document.addEventListener('mousemove', manager._onResizeMouseMove);
    document.addEventListener('mouseup', manager._onResizeMouseUp);
}

/**
 * @param {ModalManager} manager
 * @param {MouseEvent} event
 */
export function onResizeMouseMove(manager, event) {
    if (!manager._resizeDirection) return;

    const dx = event.clientX - manager._resizeStartX;
    const dy = event.clientY - manager._resizeStartY;
    const dir = manager._resizeDirection;

    let newWidth = manager._resizeStartWidth;
    let newHeight = manager._resizeStartHeight;
    let offsetX = manager._resizeStartTransformX;
    let offsetY = manager._resizeStartTransformY;

    // Вычисляем новые размеры в зависимости от направления
    if (dir.includes('e')) newWidth = manager._resizeStartWidth + dx;
    if (dir.includes('w')) {
        newWidth = manager._resizeStartWidth - dx;
        offsetX = manager._resizeStartTransformX + dx;
    }
    if (dir.includes('s')) newHeight = manager._resizeStartHeight + dy;
    if (dir.includes('n')) {
        newHeight = manager._resizeStartHeight - dy;
        offsetY = manager._resizeStartTransformY + dy;
    }

    // Ограничиваем минимальные размеры (не меньше начальных)
    if (newWidth < manager._minWidth) {
        newWidth = manager._minWidth;
        if (dir.includes('w')) {
            offsetX = manager._resizeStartTransformX + (manager._resizeStartWidth - manager._minWidth);
        }
    }
    if (newHeight < manager._minHeight) {
        newHeight = manager._minHeight;
        if (dir.includes('n')) {
            offsetY = manager._resizeStartTransformY + (manager._resizeStartHeight - manager._minHeight);
        }
    }

    // Применяем размеры
    manager.modalDialog.style.width = newWidth + 'px';
    manager.modalDialog.style.maxWidth = 'none';

    const content = manager.modalDialog.querySelector('.modal-content');
    content.style.height = newHeight + 'px';
    content.dataset.resized = 'true';

    // Обновляем transform (для ресайза слева/сверху)
    manager.modalElement.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
    manager.dragOffsetX = offsetX;
    manager.dragOffsetY = offsetY;

    // Пересчитываем прокрутку body
    manager._setModalBodyScroll();
}

/**
 * @param {ModalManager} manager
 */
export function onResizeMouseUp(manager) {
    if (!manager._resizeDirection) return;

    manager._resizeDirection = null;
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
    document.removeEventListener('mousemove', manager._onResizeMouseMove);
    document.removeEventListener('mouseup', manager._onResizeMouseUp);

    // Сбрасываем флаг с задержкой — click всплывает после mouseup,
    // и без задержки _isResizing уже false к моменту проверки в обработчике click
    requestAnimationFrame(() => {
        manager._isResizing = false;
    });

    // Сохраняем размеры в localStorage
    saveModalSize(manager);
}

// ═══════════════════════════════════════
// localStorage: сохранение/загрузка
// ═══════════════════════════════════════

/**
 * Возвращает ключ для localStorage по ID модального окна.
 * Аналогично TableManager.colWidthStorageKey — привязка к ID элемента.
 * @param {ModalManager} manager
 * @returns {string|null}
 */
export function getResizeStorageKey(manager) {
    if (!manager.modalElement?.id) return null;
    return `modalSize_${manager.modalElement.id}`;
}

/**
 * Сохраняет текущие размеры модального окна в localStorage.
 * @param {ModalManager} manager
 */
export function saveModalSize(manager) {
    const key = getResizeStorageKey(manager);
    if (!key) return;

    const content = manager.modalDialog?.querySelector('.modal-content');
    if (!content) return;

    const size = {
        width: manager.modalDialog.style.width,
        height: content.style.height
    };
    localStorage.setItem(key, JSON.stringify(size));
    if (manager.log) console.log('Modal size saved:', size);
}

/**
 * Загружает размеры модального окна из localStorage и применяет их.
 * Не применяет размер, если он меньше минимально допустимого.
 * @param {ModalManager} manager
 */
export function loadModalSize(manager) {
    const key = getResizeStorageKey(manager);
    if (!key) return;

    const stored = localStorage.getItem(key);
    if (!stored) return;

    try {
        const size = JSON.parse(stored);
        const content = manager.modalDialog?.querySelector('.modal-content');
        if (!content) return;

        if (size.width) {
            const savedWidth = parseFloat(size.width);
            if (savedWidth >= manager._minWidth) {
                manager.modalDialog.style.width = size.width;
                manager.modalDialog.style.maxWidth = 'none';
            }
        }
        if (size.height) {
            const savedHeight = parseFloat(size.height);
            if (savedHeight >= manager._minHeight) {
                content.style.height = size.height;
                content.dataset.resized = 'true';
            }
        }

        // Пересчитываем прокрутку после применения размеров
        manager._setModalBodyScroll();
        if (manager.log) console.log('Modal size loaded:', size);
    } catch (e) {
        if (manager.log) console.error('Failed to load modal size:', e);
    }
}
